import { useAppDispatch, useAppSelector } from 'src/store';
import { ProgressStatus, CMSHabitatAssayType, ProjectSamplingCadenceType, SubscriptionType, FeatureType } from 'src/shared/types';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import { useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import { isBasicProject, hasInsightsFeature, hasHabitatInsightsFeature } from 'src/shared/utils/featureHelpers';
import moment from 'moment';
import { useHabitatInsightsUploadMutation } from 'src/app/habitat-insights/state/api/habitatInsightsUploadGraphSlice';
import { NewProjectDefinitionFormState, setFormState } from '../state/newProjectDefinitionSlice';
import { useLazyProjectsQuery, useProjectDefinitionQuery } from '../../project-list/state/api/projectListGraphSlice';
import {
    useCreateProjectDefinitonMutation,
    useHasProjectDefinitionQuery,
    useLazyHasProjectDefinitionQuery,
} from '../state/api/newProjectDefinitionGraphSlice';
import { getCurrentProjectDefinitionFormState } from '../state/newProjectDefinitionSelector';
import { validateSamplingEvent } from '../components/sampling-cadence/samplingCadence.utils';

// Module-level variable to track the last loaded projectId across all hook instances
// This ensures that when the hook is used in multiple components, they share the same tracking state
let lastLoadedProjectId: string | null = null;

const useProjectDefinition = () => {
    const formState = useAppSelector(getCurrentProjectDefinitionFormState);
    const { currentCustomerId } = useCustomer();
    const [searchParams] = useSearchParams();
    const projectIdFromUrl = searchParams.get('projectId');

    // Use projectId from URL if available and formState doesn't have it yet
    const effectiveProjectId = formState.projectId || projectIdFromUrl || '';

    const { currentData: projectDefinition } = useProjectDefinitionQuery(
        {
            projectId: effectiveProjectId,
        },
        {
            skip: !effectiveProjectId,
        }
    );

    const navigate = useAppNavigation();

    const { data: projectDefinitionAvailabilityData } = useHasProjectDefinitionQuery(
        {
            projectId: effectiveProjectId,
        },
        {
            skip: !effectiveProjectId,
        }
    );

    const dispatch = useAppDispatch();

    const onFormChange = useCallback(
        (fragment: Partial<NewProjectDefinitionFormState>) => {
            dispatch(
                setFormState({
                    ...fragment,
                })
            );
        },
        [dispatch]
    );

    // Initialize projectId from URL if not already set
    useEffect(() => {
        if (projectIdFromUrl && !formState.projectId) {
            onFormChange({ projectId: projectIdFromUrl });
        }
    }, [projectIdFromUrl, formState.projectId]);

    useEffect(() => {
        if (projectDefinitionAvailabilityData) {
            onFormChange({
                projectDefinitionCompletionStatus: projectDefinitionAvailabilityData.hasProjectDefinition,
            });
        }
    }, [projectDefinitionAvailabilityData]);

    const {
        projectName,
        projectId,
        projectCode,
        features,
        country,
        projectLeadRole,
        projectLeadEmail,
        projectLeadName,
        samplingEvents,
        sampleGroups,
        habitatAssayTypes,
        projectDefinitionCompletionStatus,
        hasDefinedSampleEvents,
        habitatInsightsSites,
        habitatInsightsRunCloudJobs,
    } = formState;

    const { exists, metadata } = projectDefinitionCompletionStatus || {};

    // Edit mode is when the project definition is completed
    const isEditMode = exists && metadata?.status === ProgressStatus.COMPLETED;

    // Check if the project definition is in progress by the current user or a different user
    const isProjectStatusPending = projectDefinitionCompletionStatus && exists && metadata?.status === ProgressStatus.PENDING;
    const isInProgressByCurrentUser = isEditMode || (isProjectStatusPending && metadata?.isCreatedByCurrentUser);
    const _isInProgressByDifferentUser = isProjectStatusPending && !metadata?.isCreatedByCurrentUser;

    // User can continue the form if it's in edit mode or the project definition is in progress by the current user
    const _canCurrentUserContinueForm = isEditMode || (projectDefinitionCompletionStatus && (!exists || isInProgressByCurrentUser));

    const [createProjectDefiniton] = useCreateProjectDefinitonMutation();
    const [fetchProjects] = useLazyProjectsQuery();
    const [refetchHasProjectDefinition] = useLazyHasProjectDefinitionQuery();
    const [habitatInsightsUpload] = useHabitatInsightsUploadMutation();

    useEffect(() => {
        if (!projectDefinition) {
            return;
        }

        const currentProjectId = projectDefinition.projectId;

        // Check if form has been reset (empty projectName indicates reset)
        const isFormReset = !formState.projectName && !formState.projectCode;

        // Only load initial data if:
        // 1. We haven't loaded data for this project yet, OR
        // 2. The projectId changed (user switched to a different project), OR
        // 3. The form has been reset (even if it's the same project)
        if (lastLoadedProjectId === currentProjectId && !isFormReset) {
            return;
        }
        const fetchAndRestore = () => {
            const existingDefinition = projectDefinition;

            if (!existingDefinition) {
                return;
            }

            // Get all events with their year field - always derive from fromDate if available
            const existingDefinitionEvents =
                existingDefinition.samplingCadence?.events.map(event => {
                    let year: number;
                    // Always derive year from fromDate if available
                    if (event.fromDate && event.fromDate.trim() !== '') {
                        const parsedYear = moment(event.fromDate).year();
                        year =
                            isNaN(parsedYear) || parsedYear <= 0
                                ? event.year && event.year > 0
                                    ? event.year
                                    : moment().year()
                                : parsedYear;
                    } else {
                        // If no fromDate, use stored year if valid, otherwise current year
                        year = event.year && event.year > 0 ? event.year : moment().year();
                    }
                    return {
                        name: event.name,
                        fromDate: event.fromDate,
                        toDate: event.toDate,
                        year: year,
                    };
                }) || [];

            // On load: only restore Species Insights (EDNA) if subscription is Insights
            // This is to temporarily change to allow marking a project as Species Insights (EDNA)
            // Once the backend data is cleaned up, we can remove this filtering (DD-5209).
            const subscriptionName = existingDefinition.subscription?.name;
            const loadedFeatures = existingDefinition.features || [];
            const featuresFilteredForLoad =
                subscriptionName === SubscriptionType.INSIGHTS
                    ? loadedFeatures
                    : loadedFeatures.filter((f: string) => f !== FeatureType.EDNA);

            onFormChange({
                projectId: existingDefinition.projectId,
                features: featuresFilteredForLoad,
                subscriptionType: subscriptionName,
                country: existingDefinition.country || '',
                projectName: existingDefinition.projectName || '',
                projectLeadRole: existingDefinition.leadRole || '',
                projectLeadEmail: existingDefinition.leadEmail || '',
                projectLeadName: existingDefinition.leadName || '',
                projectCode: existingDefinition.projectCode || '',
                samplingEvents: existingDefinitionEvents,
                sampleGroups: existingDefinition.sampleGroups || [],
                habitatAssayTypes: (existingDefinition.habitatAssay || []) as Pick<
                    CMSHabitatAssayType,
                    'assayName' | 'habitatAssayKey' | 'habitatName'
                >[],
                hasDefinedSampleEvents: existingDefinition.hasDefinedSampleEvents ?? false,
            });

            // Mark this project as loaded (module-level variable shared across all hook instances)
            lastLoadedProjectId = currentProjectId;
        };
        fetchAndRestore();
    }, [projectDefinition, onFormChange, formState.projectName, formState.projectCode]);

    // Reset the loaded flag when projectId changes (user switches projects or form is reset)
    useEffect(() => {
        // If projectId is cleared (form reset), clear the tracking
        if (!formState.projectId) {
            lastLoadedProjectId = null;
        } else if (formState.projectId !== lastLoadedProjectId) {
            // If projectId changed to a different project, clear tracking
            lastLoadedProjectId = null;
        }
    }, [formState.projectId]);

    const submitProjectDefinitionForm = async () => {
        if (!currentCustomerId) {
            return;
        }

        // Calculate derived fields from events
        const eventYears = samplingEvents.map(e => e.year || moment(e.fromDate || new Date()).year());
        const minYear = eventYears.length > 0 ? Math.min(...eventYears) : moment().year();
        const maxYear = eventYears.length > 0 ? Math.max(...eventYears) : moment().year();
        const projectLifespanYears = maxYear - minYear + 1;

        // Strip the 'year' field from sampling events — it's a client-only field
        // not accepted by the GraphQL schema's ProjectDefinitionInput type
        const samplingEventPayload = samplingEvents.map(({ year: _year, ...rest }) => rest);

        try {
            await createProjectDefiniton({
                customerId: currentCustomerId,
                projectName,
                projectId,
                projectCode,
                country,
                projectLeadRole,
                projectLeadEmail,
                projectLeadName,
                projectLifespanYears,
                samplingCadenceType: ProjectSamplingCadenceType.SINGLE_YEAR,
                samplingEvent: samplingEventPayload,
                sampleGroups,
                habitatAssay: habitatAssayTypes,
                status: ProgressStatus.COMPLETED,
                hasDefinedSampleEvents,
                subscriptionType: isBasicProject(features || []) ? SubscriptionType.BASIC : SubscriptionType.INSIGHTS,
            }).unwrap();

            // Submit habitat insights sites if habitat insights feature is selected and sites are uploaded
            if (hasHabitatInsightsFeature(features || []) && habitatInsightsSites && habitatInsightsSites.length > 0) {
                try {
                    await habitatInsightsUpload({
                        portfolioId: '',
                        isHlcaProject: true,
                        project: {
                            projectId: projectId,
                            customerId: currentCustomerId,
                            projectName: projectName,
                            country: country,
                        },
                        sites: habitatInsightsSites,
                        runCloudJobs: habitatInsightsRunCloudJobs,
                    }).unwrap();
                } catch (error) {
                    console.error('Habitat insights upload failed:', error);
                    // Don't throw - project definition was created successfully
                }
            }
        } catch (error) {
            console.error('Error submitting project definition:', error);
            // Re-throw the error so the calling code can handle it
            throw error;
        }
    };

    const saveProjectDefinitionForm = async () => {
        if (!currentCustomerId || !projectId) {
            return;
        }

        // Calculate derived fields from events
        const eventYears = samplingEvents.map(e => e.year || moment(e.fromDate || new Date()).year());
        const minYear = eventYears.length > 0 ? Math.min(...eventYears) : moment().year();
        const maxYear = eventYears.length > 0 ? Math.max(...eventYears) : moment().year();
        const projectLifespanYears = maxYear - minYear + 1;

        // Strip the 'year' field from sampling events — it's a client-only field
        // not accepted by the GraphQL schema's ProjectDefinitionInput type
        const samplingEventPayload = samplingEvents.map(({ year: _year, ...rest }) => rest);

        try {
            await createProjectDefiniton({
                customerId: currentCustomerId,
                projectName,
                projectId,
                projectCode,
                country,
                projectLeadRole,
                projectLeadEmail,
                projectLeadName,
                samplingCadenceType: ProjectSamplingCadenceType.SINGLE_YEAR,
                projectLifespanYears,
                samplingEvent: samplingEventPayload,
                sampleGroups,
                habitatAssay: habitatAssayTypes,
                status: ProgressStatus.PENDING,
                hasDefinedSampleEvents,
                subscriptionType: isBasicProject(features || []) ? SubscriptionType.BASIC : SubscriptionType.INSIGHTS,
            }).unwrap();

            // Submit habitat insights sites if habitat insights feature is selected and sites are uploaded
            if (hasHabitatInsightsFeature(features || []) && habitatInsightsSites && habitatInsightsSites.length > 0) {
                try {
                    await habitatInsightsUpload({
                        portfolioId: '',
                        isHlcaProject: true,
                        project: {
                            projectId: projectId,
                            customerId: currentCustomerId,
                            projectName: projectName,
                            country: country,
                        },
                        sites: habitatInsightsSites,
                        runCloudJobs: habitatInsightsRunCloudJobs,
                    }).unwrap();
                } catch (error) {
                    console.error('Habitat insights upload failed:', error);
                    // Don't throw - project definition was saved successfully
                }
            }

            await fetchProjects({
                customerId: currentCustomerId,
            }).unwrap();

            await refetchHasProjectDefinition({
                projectId,
            }).unwrap();

            navigate.toOrganisationManagement();
        } catch (error) {
            console.error('Error saving project definition:', error);
            // Re-throw the error so the calling code can handle it
            throw error;
        }
    };

    const isFormValid = () => {
        // Project details validation
        const mandatoryData = [projectName, country, projectLeadRole];
        const isProjectDetailsValid = mandatoryData.every(Boolean);

        // Project aims validation - only required if EDNA feature is selected
        // Note: Sample groups and test types are no longer required
        const hasEDNA = hasInsightsFeature(features || []);
        const isProjectAimsValid = true; // Sample groups and test types are optional

        // Sampling cadence validation - only required if EDNA feature is selected
        let isSamplingCadenceValid = true;
        if (hasEDNA) {
            const isBasic = isBasicProject(features || []);
            const isSamplingEventsKnown = formState.hasDefinedSampleEvents;

            if (isBasic && !isSamplingEventsKnown) {
                isSamplingCadenceValid = true;
            } else if (samplingEvents.length > 0) {
                // Validate all events if any exist
                const validEvents = samplingEvents.map((event, index) => !validateSamplingEvent(formState, index).hasError);
                isSamplingCadenceValid = validEvents.every(Boolean);
            }
            // If samplingEvents.length === 0, it's valid (no requirement for at least one event)
        }

        return isProjectDetailsValid && isProjectAimsValid && isSamplingCadenceValid;
    };

    return {
        isFormValid,
        submitProjectDefinitionForm,
        saveProjectDefinitionForm,
        onFormChange,
        formState,
        isBasicAndGenericProject: false,
        isEditMode,
    };
};

export default useProjectDefinition;
